'use client';

import type { PodDetailBucket, PodDetailSection } from '@slims/ui';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api } from '@/lib/utils/api';
import { useAdminScopeStore } from '@/stores/admin-scope-store';

/**
 * Produzione e consumo di un punto di connessione, ciascuno per livello
 * (`GET /api/energy/pod-detail`).
 *
 * A differenza di `usePodLayers`, che prende un `meterId` e restituisce una sola
 * direzione, qui il soggetto è il POD: le due direzioni tornano insieme perché è
 * il confronto fra loro la ragione per aprire il dettaglio.
 */
export interface PodDetailResponse {
	pod: {
		id: string;
		codice: string;
		memberId: string;
		memberName: string | null;
		dso: string | null;
		cabinaPrimaria: string | null;
	};
	bucket: PodDetailBucket;
	from: string;
	to: string;
	directions: PodDetailSection[];
}

export interface UsePodDetailParams {
	podId?: string | null;
	from?: string | null;
	/** Giorno civile finale, incluso. */
	to?: string | null;
	bucket?: PodDetailBucket;
	/**
	 * Configurazione da leggere. Assente = lo scope amministrativo corrente; va
	 * passato dove la pagina ne mostra più di una affiancate (la dashboard), o
	 * l'esploso risponderebbe con i POD della configurazione sbagliata.
	 */
	orgId?: string | null;
	enabled?: boolean;
}

/** ADR-006: backed by React Query. */
export function usePodDetail({
	podId,
	from,
	to,
	bucket = 'slot',
	orgId: orgIdOverride,
	enabled = true,
}: UsePodDetailParams) {
	const scopeOrgId = useAdminScopeStore((s) => s.configId);
	const orgId = orgIdOverride ?? scopeOrgId;
	const active = !!podId && !!from && !!to && enabled && !!orgId;

	const { data, isLoading, error, refetch: rqRefetch } = useQuery({
		queryKey: ['pod-detail', orgId, podId, from, to, bucket],
		enabled: active,
		queryFn: () => {
			const params = new URLSearchParams({
				podId: podId as string,
				from: from as string,
				to: to as string,
				bucket,
				orgId: orgId as string,
			});
			return api.get<PodDetailResponse>(`/api/energy/pod-detail?${params.toString()}`);
		},
	});

	const refetch = useCallback(async () => {
		await rqRefetch();
	}, [rqRefetch]);

	return {
		data: data ?? null,
		isLoading: active ? isLoading : false,
		error: error
			? error instanceof Error
				? error.message
				: 'Errore nel caricamento del dettaglio POD'
			: null,
		refetch,
	};
}
